/**
 * Shared FactSet intent transport.
 *
 * Houses the postMessage plumbing used by every FactSet namespace bridge
 * (PowerPoint's `intentBridgeService.ts`, Excel's `spreadsheetIntents.ts`,
 * the host-context probe, etc.). Keeping this in one module ensures every
 * namespace observes identical origin resolution, timeout handling, and
 * response parsing, and lets transport-level improvements (metrics, retries,
 * tracing) land in a single place.
 *
 * This module is intentionally host-agnostic: it knows nothing about
 * presentations, spreadsheets, or any specific intent payload shape. Callers
 * layer namespace-specific types and wrappers on top.
 *
 * Per-call telemetry is emitted via `createLogger("FactSetIntent")` from
 * `raiseIntent`. Each call gets a monotonic sequence number so a
 * `[FactSetIntent] #42 → factset.Spreadsheet.GetRangeInfo` (start) line can
 * be correlated with its `#42 ← ... in 87ms` (end) or `#42 ✗ ... timeout`
 * line, even when several intents are in flight at once. Logs respect the
 * shared logger's level gating — info/debug fire in dev, warn/error always.
 */

import { createLogger } from "../utils/logger";

export const DEFAULT_APP_ID = { appId: "com.factset.workstation" };
export const DEFAULT_TIMEOUT_MS = 15_000;

/**
 * AppIdentifier shape used by `raiseIntent`'s `appId` override. Mirrors
 * FDC3's `AppIdentifier` minus the optional `instanceId` we don't use.
 */
export interface IntentAppId {
  appId: string;
}

const log = createLogger("FactSetIntent");

let intentCallCounter = 0;

/**
 * Toggle for transport-level call logging. Defaults to on; can be flipped
 * by callers (e.g. a stress test) to silence the per-call info lines
 * without touching the dev/prod gate inside the shared logger. Errors
 * always log regardless of this flag.
 */
let loggingEnabled = true;
export function setIntentTransportLoggingEnabled(enabled: boolean): void {
  loggingEnabled = enabled;
}

/**
 * Cap on string-content length when summarising request / response bodies.
 * Long values (giant CSVs, full pasted formulas, base64 image blobs) are
 * truncated with a `...` tail so the log line stays grep-friendly.
 */
const SUMMARY_VALUE_TRUNCATE = 80;

/**
 * Cap on array length when summarising. Above this, we log
 * `<N items, first: ...>` instead of the whole array. Tuned so a 7×5
 * `contents[]` (35 cells) inlines fully but a 1000-row CSV block doesn't.
 */
const SUMMARY_ARRAY_PREVIEW_LIMIT = 3;
const BASE64_LOG_KEY_PATTERN = /(base64|dataUri)/i;
const ACTIVE_GRAPH_XML_LOG_KEY_PATTERN = /^(fdsChartXml|activeGraphContent)$/i;
const LOCAL_PATH_LOG_KEY_PATTERN =
  /^(filePath|folderPath|fullFileName|filename)$/i;

/** Return a redacted one-line summary for fields that can expose large or local-only data. */
function summarizeSensitiveFieldForLog(key: string, value: unknown): unknown {
  if (BASE64_LOG_KEY_PATTERN.test(key)) {
    return typeof value === "string" ? `<base64 ${value.length} chars>` : "<base64>";
  }
  if (ACTIVE_GRAPH_XML_LOG_KEY_PATTERN.test(key)) {
    return typeof value === "string"
      ? `<active-graph-payload ${value.length} chars>`
      : "<active-graph-payload>";
  }
  if (LOCAL_PATH_LOG_KEY_PATTERN.test(key)) {
    return "<local-path>";
  }
  return undefined;
}

/** Remove sensitive payload values from errors before they reach shared intent logs. */
function sanitizeErrorForLog(error: unknown, context: Record<string, unknown>): string {
  let message = error instanceof Error ? error.message : String(error);
  const visit = (value: unknown, depth = 0): void => {
    if (depth >= 5 || !value || typeof value !== "object") return;
    for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
      if (typeof nested === "string" && nested.length > 0) {
        const replacement = LOCAL_PATH_LOG_KEY_PATTERN.test(key)
          ? "<local-path>"
          : BASE64_LOG_KEY_PATTERN.test(key)
            ? "<base64>"
            : ACTIVE_GRAPH_XML_LOG_KEY_PATTERN.test(key)
              ? "<active-graph-payload>"
              : undefined;
        if (replacement) {
          message = message.split(nested).join(replacement);
          continue;
        }
      }
      if (nested && typeof nested === "object") {
        visit(nested, depth + 1);
      }
    }
  };
  visit(context);
  return message;
}

/**
 * Build a compact, log-friendly summary of an intent's request or response
 * context. Designed so a glance at the console gives a sense of what was
 * sent without the overhead of pretty-printing 10KB of cell payload. Debug
 * logs use the same redacted summary so base64 bytes and local paths never
 * reach the console.
 *
 *  - Strings longer than `SUMMARY_VALUE_TRUNCATE` are truncated with `…`.
 *  - Arrays longer than `SUMMARY_ARRAY_PREVIEW_LIMIT` collapse to
 *    `<N items: [first, second, ...]>` form.
 *  - Recurses into objects but caps depth at 4 to avoid pathological
 *    inputs blowing up the prefix line.
 *  - Nullish, primitive, and unrepresentable values are returned as-is
 *    (or stringified) so JSON.stringify on the result never throws.
 */
function summarizeForLog(value: unknown, depth: number = 0): unknown {
  if (depth >= 4) return "<…>";
  if (value === null || value === undefined) return value;
  const t = typeof value;
  if (t === "string") {
    const s = value as string;
    return s.length > SUMMARY_VALUE_TRUNCATE
      ? `${s.slice(0, SUMMARY_VALUE_TRUNCATE)}… (${s.length} chars)`
      : s;
  }
  if (t === "number" || t === "boolean" || t === "bigint") return value;
  if (t === "function") return "<fn>";
  if (Array.isArray(value)) {
    if (value.length === 0) return [];
    if (value.length <= SUMMARY_ARRAY_PREVIEW_LIMIT) {
      return value.map((v) => summarizeForLog(v, depth + 1));
    }
    return {
      __array: true,
      length: value.length,
      preview: value
        .slice(0, SUMMARY_ARRAY_PREVIEW_LIMIT)
        .map((v) => summarizeForLog(v, depth + 1)),
    };
  }
  if (t === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const sensitiveSummary = summarizeSensitiveFieldForLog(k, v);
      out[k] =
        sensitiveSummary !== undefined ? sensitiveSummary : summarizeForLog(v, depth + 1);
    }
    return out;
  }
  try {
    return String(value);
  } catch {
    return "<unrepresentable>";
  }
}

function normalizeOrigin(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  try {
    return new URL(trimmed).origin;
  } catch {
    return undefined;
  }
}

function resolveParentOrigin(): string | null {
  if (typeof window === "undefined") return null;

  const ancestorOrigins = (
    window.location as Location & { ancestorOrigins?: DOMStringList }
  ).ancestorOrigins;
  if (ancestorOrigins && ancestorOrigins.length > 0) {
    const fromAncestor = normalizeOrigin(ancestorOrigins[0]);
    if (fromAncestor) {
      return fromAncestor;
    }
  }

  const fromReferrer = normalizeOrigin(typeof document !== "undefined" ? document.referrer : undefined);
  return fromReferrer ?? null;
}

function canUseWindowBridge(): boolean {
  if (typeof window === "undefined") return false;
  if (!window.parent || window.parent === window) return false;
  return typeof window.parent.postMessage === "function";
}

/**
 * Resolve the trusted parent-frame origin, throwing if the bridge is not
 * available. Exposed for bridges (e.g. the PowerPoint batch handler) that
 * need to split soft vs. hard timeouts and therefore drive
 * `postMessage` / listener setup manually instead of calling `raiseIntent`.
 */
export function getBridgeParentOriginOrThrow(): string {
  if (!canUseWindowBridge()) {
    throw new Error("FactSet intent bridge unavailable in this runtime.");
  }
  const parentOrigin = resolveParentOrigin();
  if (!parentOrigin) {
    throw new Error("Could not determine a trusted parent origin for FactSet intent bridge.");
  }
  return parentOrigin;
}

/**
 * Mint a unique `respondAt` event name used to correlate a postMessage
 * request with its reply. Exposed for the same advanced-timeout use cases
 * as `getBridgeParentOriginOrThrow`.
 */
export function generateRespondAt(): string {
  return `factset.raiseIntent.${Date.now()}.${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Build a `DOMException` with `name === "AbortError"` when available,
 * falling back to a plain `Error` so the function works in non-DOM
 * runtimes (test envs, SSR). Callers — notably `ExcelApp.tsx` — branch
 * on `e instanceof DOMException && e.name === "AbortError"` to
 * distinguish user-cancel from real failures, so producing the standard
 * shape here is what makes that branch fire.
 */
function makeAbortError(intentName: string): Error {
  const message = `"${intentName}" was aborted`;
  if (typeof DOMException !== "undefined") {
    return new DOMException(message, "AbortError");
  }
  const err = new Error(message);
  err.name = "AbortError";
  return err;
}

const TIMEOUT_MESSAGE_MARKER = "Timed out after";

/**
 * Is this rejection our own soft timeout rather than a host-reported failure?
 *
 * The distinction matters because the timeout does not cancel anything: the
 * host keeps working behind its own progress dialog and the user's data still
 * lands. Callers use this to report "still running" instead of telling the
 * user to retry work that is already happening.
 *
 * Matching on the message is the only signal the bridge gives us, so the check
 * lives next to the `reject` that produces it. `includes` rather than a prefix
 * test so a wrapping layer that prepends context still routes correctly.
 */
export function isIntentTimeoutError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return message.includes(TIMEOUT_MESSAGE_MARKER);
}

export function waitForIntentResponse(
  respondAt: string,
  intentName: string,
  timeoutMs: number,
  parentOrigin: string,
  signal?: AbortSignal
): Promise<unknown> {
  return new Promise<unknown>((resolve, reject) => {
    if (signal?.aborted) {
      reject(makeAbortError(intentName));
      return;
    }

    const timer = window.setTimeout(() => {
      cleanup();
      reject(
        new Error(
          `${TIMEOUT_MESSAGE_MARKER} ${timeoutMs}ms while waiting for "${intentName}"`
        )
      );
    }, timeoutMs);

    function cleanup() {
      window.clearTimeout(timer);
      window.removeEventListener("message", handler);
      if (signal && onAbort) {
        signal.removeEventListener("abort", onAbort);
      }
    }

    // Captured separately so `cleanup` can detach it. The host has no
    // way to receive a cancel signal — once the request is in flight,
    // the best we can do client-side is stop waiting for the reply and
    // let any late response be ignored by the listener-removed handler.
    const onAbort = signal
      ? () => {
          cleanup();
          reject(makeAbortError(intentName));
        }
      : null;
    if (signal && onAbort) {
      signal.addEventListener("abort", onAbort, { once: true });
    }

    function handler(event: MessageEvent) {
      if (event.source !== window.parent) return;
      if (event.origin !== parentOrigin) return;
      if (event.data?.eventName !== respondAt) return;
      cleanup();
      if (event.data?.error) {
        reject(new Error(String(event.data.error)));
        return;
      }
      resolve(event.data?.result?.context ?? event.data);
    }

    window.addEventListener("message", handler);
  });
}

export function postIntentMessage(
  respondAt: string,
  intentName: string,
  context: Record<string, unknown>,
  parentOrigin: string,
  appId: IntentAppId = DEFAULT_APP_ID
): void {
  window.parent.postMessage(
    {
      eventName: "factset.raiseIntent",
      respondAt,
      intent: { intent: intentName, appId },
      context,
    },
    parentOrigin
  );
}

/**
 * Raise a single FactSet intent and resolve with the parsed response context.
 *
 * Throws if the bridge is unavailable, the parent origin cannot be resolved,
 * the parent replies with an error, or the timeout elapses.
 *
 * Emits transport-level telemetry via `createLogger("FactSetIntent")`:
 *   - `#N → <intent>` with a request-context summary at the start
 *   - `#N ← <intent> in <ms>ms` with a response summary on success
 *   - `#N ✗ <intent> in <ms>ms` with the error on rejection (always
 *     logged, even when `setIntentTransportLoggingEnabled(false)` is set,
 *     because failures are too costly to silence)
 *
 * Redacted request and response summaries are also passed to `log.debug`.
 * See `summarizeForLog` for how payloads are condensed.
 *
 * Pass `signal` to make the wait abortable (e.g. for slow charts via
 * `factset.PreviewChart`). When the signal aborts, the returned promise
 * rejects with a `DOMException` named `AbortError`. The host has no way
 * to receive a cancel — late replies after abort are dropped silently
 * by the now-detached message listener.
 */
export function raiseIntent(
  intentName: string,
  context: Record<string, unknown>,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
  appId: IntentAppId = DEFAULT_APP_ID,
  signal?: AbortSignal
): Promise<unknown> {
  const parentOrigin = getBridgeParentOriginOrThrow();
  const respondAt = generateRespondAt();
  const seq = ++intentCallCounter;
  const startedAt =
    typeof performance !== "undefined" && typeof performance.now === "function"
      ? performance.now()
      : Date.now();

  if (loggingEnabled) {
    const ctxSummary = summarizeForLog(context);
    // Surface the appId override explicitly when callers route to anything
    // other than the default workstation target (e.g. `OI Remote Iframe`
    // for `factset.PreviewChart`) — silent routing changes are too easy
    // to miss in the field.
    const targetSuffix =
      appId.appId === DEFAULT_APP_ID.appId ? "" : ` [target: ${appId.appId}]`;
    log.info(
      `#${seq} → ${intentName}${targetSuffix}`,
      ctxSummary
    );
    log.debug(`#${seq} request (redacted)`, {
      intentName,
      context: summarizeForLog(context),
      timeoutMs,
      appId,
    });
  }

  const responsePromise = waitForIntentResponse(respondAt, intentName, timeoutMs, parentOrigin, signal);
  postIntentMessage(respondAt, intentName, context, parentOrigin, appId);

  return responsePromise.then(
    (response) => {
      const elapsed = Math.round(
        (typeof performance !== "undefined" && typeof performance.now === "function"
          ? performance.now()
          : Date.now()) - startedAt
      );
      if (loggingEnabled) {
        log.info(
          `#${seq} ← ${intentName} in ${elapsed}ms`,
          summarizeForLog(response)
        );
        log.debug(`#${seq} response (redacted)`, {
          intentName,
          response: summarizeForLog(response),
        });
      }
      return response;
    },
    (err) => {
      const elapsed = Math.round(
        (typeof performance !== "undefined" && typeof performance.now === "function"
          ? performance.now()
          : Date.now()) - startedAt
      );
      // Always log errors — they're rare and high-signal. The request
      // context is included so a 1-line read of the failure tells the
      // dev *which* call timed out without having to grep back to the
      // `#N →` line.
      const safeError = sanitizeErrorForLog(err, context);
      log.warn(
        `#${seq} ✗ ${intentName} in ${elapsed}ms`,
        safeError,
        summarizeForLog(context)
      );
      log.debug(`#${seq} error (redacted)`, {
        intentName,
        context: summarizeForLog(context),
        error: safeError,
      });
      throw err;
    }
  );
}

/**
 * Cheap runtime check: is the window-level postMessage bridge wired up such
 * that `raiseIntent` has a chance of succeeding?
 *
 * Returns `false` when running outside a browser, when there is no parent
 * window, or when the parent is the same frame (e.g. standalone taskpane).
 */
export function isFactSetIntentBridgeAvailable(): boolean {
  return canUseWindowBridge();
}
